/* ============================================================================
 * <vc-supply-picker> — Vitaceuticals buy-box controller
 *
 * A custom element that wraps Subscribe/Once mode tabs + Single/Routine/Bulk
 * tier cards + the product <form>. Owns:
 *   • Mode toggle (subscribe ⇄ one-time)
 *   • Tier selection (clicking a card sets the form's variant + selling_plan)
 *   • Filtering tiers by mode (subscribe tiers visible in subscribe mode, etc.)
 *   • Broadcasting 'vc:variant-change' events for sticky bars, galleries, etc.
 *
 * Markup contract (any of these can be missing — the element degrades gracefully):
 *
 *   <vc-supply-picker>
 *     <!-- Mode tabs (subscribe/once) -->
 *     <div data-vc-mode-tabs>
 *       <button data-vc-mode="subscribe" aria-selected="true">Subscribe & Save</button>
 *       <button data-vc-mode="once">One-time</button>
 *     </div>
 *
 *     <!-- Tier cards -->
 *     <div data-vc-tiers>
 *       <button data-vc-tier
 *               data-vc-tier-mode="subscribe"        ← required: which mode this belongs to
 *               data-variant-id="12345"              ← required: the Shopify variant ID
 *               data-selling-plan-id="678"           ← optional: Loop selling plan ID (subscribe only)
 *               data-price="2240"                    ← optional: minor units, for sticky bar
 *               data-compare="2800"                  ← optional: minor units, compare-at
 *               data-image="https://...">            ← optional: variant image URL
 *         …card content…
 *       </button>
 *     </div>
 *
 *     <!-- Product form (Shopify's standard form, or one rendered by {% form 'product' %}) -->
 *     <form data-vc-form action="/cart/add" method="post">
 *       <input type="hidden" name="id" data-vc-form-id-input>
 *       <!-- selling_plan input gets injected/removed dynamically -->
 *       …rest of the form, ATC button…
 *     </form>
 *   </vc-supply-picker>
 *
 * Events emitted:
 *   'vc:variant-change' (bubbles, composed) — detail = { variantId, sellingPlanId, mode, priceCents, compareCents, image }
 *
 * Re-init: handles 'shopify:section:load' / 'shopify:section:unload' so the
 * theme editor's live reload works without leaving stale handlers.
 * ============================================================================ */

if (!customElements.get('vc-supply-picker')) {
  customElements.define(
    'vc-supply-picker',
    class VcSupplyPicker extends HTMLElement {
      connectedCallback() {
        this.mode = this.getAttribute('mode') || 'subscribe';
        this._readEls();
        this._bindEvents();
        // Initial state — pick first visible tier matching the default mode.
        this._applyMode(this.mode, { silent: true });
        const initial = this._tiers.find(t => t.dataset.vcActive === 'true' && t.dataset.vcTierMode === this.mode)
                     || this._tiers.find(t => t.dataset.vcTierMode === this.mode);
        if (initial) this._selectTier(initial, { silent: true });
      }

      disconnectedCallback() {
        if (this._unbindMode) this._unbindMode();
        if (this._unbindTier) this._unbindTier();
        if (this._unbindSize) this._unbindSize();
      }

      // ─── internals ───────────────────────────────────────────────────

      _readEls() {
        this._modeTabs = Array.from(this.querySelectorAll('[data-vc-mode]'));
        this._tiers    = Array.from(this.querySelectorAll('[data-vc-tier]'));
        this._form     = this.querySelector('[data-vc-form]') || this.querySelector('form') || this.closest('form');
        this._idInput  = this._form && this._form.querySelector('[data-vc-form-id-input]') || this._form && this._form.querySelector('input[name="id"]');
        // Size picker (optional): the #pz-size-* container of .pk-opt[data-size] buttons.
        this._sizeWrap = this.querySelector('[id^="pz-size-"]');
        this._sizeBtns = this._sizeWrap ? Array.from(this._sizeWrap.querySelectorAll('.pk-opt')) : [];
        const activeSizeBtn = this._sizeBtns.find(b => b.classList.contains('on')) || this._sizeBtns[0];
        this._activeSize = activeSizeBtn ? (activeSizeBtn.dataset.size || null) : null;
      }

      _bindEvents() {
        const onModeClick = e => {
          const btn = e.target.closest('[data-vc-mode]');
          if (!btn || !this.contains(btn)) return;
          e.preventDefault();
          this._applyMode(btn.getAttribute('data-vc-mode'));
        };
        const onTierClick = e => {
          const card = e.target.closest('[data-vc-tier]');
          if (!card || !this.contains(card)) return;
          e.preventDefault();
          this._selectTier(card);
        };
        const onSizeClick = e => {
          const btn = e.target.closest('.pk-opt');
          if (!btn || !this._sizeWrap || !this._sizeWrap.contains(btn)) return;
          e.preventDefault();
          this._selectSize(btn);
        };
        this.addEventListener('click', onModeClick);
        this.addEventListener('click', onTierClick);
        this.addEventListener('click', onSizeClick);
        this._unbindMode = () => this.removeEventListener('click', onModeClick);
        this._unbindTier = () => this.removeEventListener('click', onTierClick);
        this._unbindSize = () => this.removeEventListener('click', onSizeClick);
      }

      _selectSize(btn) {
        this._activeSize = btn.dataset.size || null;
        this._sizeBtns.forEach(b => b.classList.toggle('on', b === btn));
        // Re-filter tiers for the new size (keeps current mode) and re-select first visible.
        this._applyMode(this.mode);
      }

      _applyMode(mode, { silent = false } = {}) {
        this.mode = mode;
        this.setAttribute('mode', mode);

        // Update tabs
        this._modeTabs.forEach(t => {
          const isActive = t.getAttribute('data-vc-mode') === mode;
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          t.classList.toggle('is-active', isActive);
        });

        // Filter tiers by mode AND active size (hide non-matching, show matching)
        let firstMatch = null;
        this._tiers.forEach(t => {
          const tierMode = t.getAttribute('data-vc-tier-mode');
          // If no tier mode set, treat as visible in both modes.
          const matchesMode = !tierMode || tierMode === mode;
          // If no size picker / tier has no size, treat as visible for any size.
          const tierSize = t.getAttribute('data-size');
          const matchesSize = !this._activeSize || !tierSize || tierSize === this._activeSize;
          const matches = matchesMode && matchesSize;
          t.hidden = !matches;
          if (matches && !firstMatch) firstMatch = t;
          if (!matches) t.setAttribute('aria-checked', 'false');
        });

        // Re-select: keep current variant if still visible, otherwise pick first
        const currentlySelected = this._tiers.find(t => t.getAttribute('aria-checked') === 'true' && !t.hidden);
        const target = currentlySelected || firstMatch;
        if (target) this._selectTier(target, { silent });
      }

      _selectTier(card, { silent = false } = {}) {
        this._tiers.forEach(t => {
          const isActive = t === card;
          t.setAttribute('aria-checked', isActive ? 'true' : 'false');
          t.classList.toggle('is-active', isActive);
        });

        // Sync form
        const variantId    = card.getAttribute('data-variant-id');
        const sellingPlan  = card.getAttribute('data-selling-plan-id');
        const priceCents   = parseInt(card.getAttribute('data-price') || '0', 10) || null;
        const compareCents = parseInt(card.getAttribute('data-compare') || '0', 10) || null;
        const image        = card.getAttribute('data-image') || null;

        if (variantId && this._idInput) {
          this._idInput.value = variantId;
        }
        this._syncSellingPlan(sellingPlan);

        if (silent) return;
        this.dispatchEvent(new CustomEvent('vc:variant-change', {
          bubbles: true, composed: true,
          detail: { variantId, sellingPlanId: sellingPlan || null, mode: this.mode, priceCents, compareCents, image, card },
        }));
      }

      _syncSellingPlan(planId) {
        if (!this._form) return;
        let inp = this._form.querySelector('input[name="selling_plan"]');
        const wantPlan = this.mode === 'subscribe' && planId && planId !== '';
        if (wantPlan) {
          if (!inp) {
            inp = document.createElement('input');
            inp.type = 'hidden';
            inp.name = 'selling_plan';
            this._form.appendChild(inp);
          }
          inp.value = planId;
        } else if (inp) {
          inp.remove();
        }
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * Toggle-price sync for the _vc-buy-zone layout.
 *  - Switching mode  -> BOTH toggle tabs show their mode's CHEAPEST price
 *    (from data-cheapest), so you always start at the lowest, never the last
 *    tier you were on.
 *  - Picking a tier  -> only the ACTIVE tab reflects that tier; the inactive
 *    tab stays on its cheapest.
 * No-ops on layouts without .sto-pr / .pk-n.
 * ------------------------------------------------------------------------- */
(function () {
  var prevMode = new WeakMap();
  function setText(el, txt) {
    if (!el || txt == null) return;
    var tn = Array.prototype.slice.call(el.childNodes).filter(function (n) { return n.nodeType === 3; })[0];
    if (tn) tn.nodeValue = txt; else el.appendChild(document.createTextNode(txt));
  }
  document.addEventListener('vc:variant-change', function (e) {
    var picker = (e.target && e.target.closest) ? e.target.closest('vc-supply-picker') : null;
    if (!picker) return;
    var d = e.detail || {};
    var subEl = picker.querySelector('[id^="pdp-sub-pr-"]');
    var onceEl = picker.querySelector('[id^="pdp-once-pr-"]');
    if (!subEl && !onceEl) return;
    var prev = prevMode.get(picker);
    if (prev !== d.mode) {
      // mode switched (or first interaction): both tabs -> their cheapest
      if (subEl) setText(subEl, subEl.getAttribute('data-cheapest'));
      if (onceEl) setText(onceEl, onceEl.getAttribute('data-cheapest'));
    } else {
      // same mode, a tier was picked: active tab -> that tier, inactive -> cheapest
      var card = d.card;
      var price = card && card.querySelector('.pk-n') ? card.querySelector('.pk-n').textContent : null;
      var activeEl = d.mode === 'subscribe' ? subEl : onceEl;
      var inactiveEl = d.mode === 'subscribe' ? onceEl : subEl;
      if (activeEl && price) setText(activeEl, price);
      if (inactiveEl) setText(inactiveEl, inactiveEl.getAttribute('data-cheapest'));
    }
    prevMode.set(picker, d.mode);
  });
})();
